/** @jsx isolateComponent */
import { useState } from 'react';

import { isolateComponent } from '@zapier/style-encapsulation';
import { Button, Colors, Typography } from '@zapier/design-system';

import { SizeType } from '@zapier/product-get-help/src/types';

import PositiveFeedback from '../PositiveFeedback';

type FeedbackProps = {
  /** Called when you click 'No, what’s next?' or 'Yes, thank you' and then 'see more solutions'. */
  onGoToNextStep: () => void;
  /** Called when you click a link in the positive feedback "Help Center" box. */
  onClickHelpCenterLink: (href: string) => void;
  /** Called when you click 'No, what’s next?' button. */
  onClickNoWhatsNext: () => void;
  /** Size to display. */
  size: SizeType;
};

const Styles = {
  inner: {
    alignItems: 'center',
    display: 'grid',
    gridGap: 20,
    gridTemplateColumns: '1fr min-content min-content',
    justifyItems: 'end',
  },
  innerSmall: {
    gridTemplateColumns: '1fr',
    justifyItems: 'start',
  },
  resolvedButtonsText: {
    ...Typography.paragraph3Bold,
    color: Colors.gray,
  },
  root: {
    display: 'grid',
    gridGap: 60,
  },
};

const RebrandStyles = {
  resolvedButtonsText: {
    ...Typography.paragraph3Semibold,
    color: Colors.neutral800,
  },
};

/**
 * Displayed on all but the last stage of the `<HelpBlock>` component.
 */
const Feedback = ({
  onGoToNextStep,
  onClickHelpCenterLink,
  onClickNoWhatsNext,
  size,
}: FeedbackProps) => {
  const [didSolve, setDidSolve] = useState<boolean>();

  const didProvideFeedback = typeof didSolve === 'boolean';
  const yesColor = didProvideFeedback ? 'primary-alt' : 'secondary';
  const onClickYes = () => {
    setDidSolve(true);
  };

  const onClickNo = () => {
    setDidSolve(false);
    onClickNoWhatsNext();
    onGoToNextStep();
  };

  return (
    <div css={Styles.root}>
      <div css={[Styles.inner, size === 'small' && Styles.innerSmall]}>
        <p css={RebrandStyles.resolvedButtonsText}>
          Did this solve your issue?
        </p>
        <Button color={yesColor} onClick={onClickYes}>
          Yes, thank you
        </Button>
        <Button
          color="primary"
          disabled={didProvideFeedback}
          onClick={onClickNo}
        >
          No, what’s next?
        </Button>
      </div>
      {didSolve && (
        <PositiveFeedback
          onClickHelpCenterLink={href => onClickHelpCenterLink(href)}
          onGoToNextStep={onGoToNextStep}
        />
      )}
    </div>
  );
};

export default Feedback;
